import type { DuelSide, Fixture } from '@h2h/shared';
import { Check } from '../ui/icons';
import { cn } from '../../lib/cn';
import { useHaptics } from '../../hooks/useHaptics';
import { parseRiderName, riderSubtitle } from '../../lib/riderDisplay';
import { RiderAvatar } from './cycling-ui/RiderAvatar';

/**
 * One terrain-matched duel on a cycling stage — binary A/B pick with mockup styling.
 * The first uncalled duel reads as "active" (dark CALL IT chip); called duels show
 * the backed rider in green with a check and the other rider muted as "2nd".
 */
export function DuelPickCard({
  fixture,
  index,
  pick,
  active = false,
  disabled = false,
  onPick,
  id
}: {
  fixture: Fixture;
  /** 0-based position in the slate (for the "Duel N" chip). */
  index: number;
  pick: DuelSide | undefined;
  /** First uncalled duel — stronger border + CALL IT chip. */
  active?: boolean;
  disabled?: boolean;
  onPick: (side: DuelSide) => void;
  id?: string;
}) {
  const { impact } = useHaptics();
  const aName = fixture.riderA?.riderName ?? fixture.homeTeam;
  const bName = fixture.riderB?.riderName ?? fixture.awayTeam;
  const called = pick != null;

  const choose = (side: DuelSide) => {
    if (disabled) return;
    impact('selection');
    onPick(side);
  };

  return (
    <div
      id={id}
      className={cn(
        'overflow-hidden rounded-[18px] bg-white px-[11px] pb-[11px] pt-2.5',
        disabled
          ? 'border border-black/[0.07]'
          : called
            ? 'border border-accent/30'
            : active
              ? 'border-[1.5px] border-[rgba(21,21,27,0.22)] shadow-[0_14px_30px_-20px_rgba(20,20,50,0.4)]'
              : 'border border-black/[0.07]'
      )}
    >
      <div className="mb-2 flex items-center justify-between px-0.5">
        <span className="text-[10.5px] font-extrabold tracking-[0.12em] text-[#B4B4BC]">DUEL {index + 1}</span>
        {called ? (
          <span className="flex items-center gap-[5px] rounded-full bg-accent/[0.12] px-[9px] py-1">
            <Check width={11} height={11} strokeWidth={3.2} className="text-accent-ink" />
            <span className="text-[10px] font-extrabold tracking-[0.04em] text-accent-ink">CALLED</span>
          </span>
        ) : active ? (
          <span className="flex items-center gap-[5px] rounded-full bg-night px-[9px] py-1">
            <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" />
            <span className="text-[10px] font-extrabold tracking-[0.04em] text-white">CALL IT</span>
          </span>
        ) : (
          <span className="text-[10px] font-bold tracking-[0.04em] text-[#C4C4CC]">TAP TO CALL</span>
        )}
      </div>

      <PickRow
        name={aName}
        team={fixture.riderA?.teamName}
        nationality={fixture.riderA?.nationality}
        selected={pick === 'A'}
        called={called}
        disabled={disabled}
        onClick={() => choose('A')}
      />

      <VsDivider />

      <PickRow
        name={bName}
        team={fixture.riderB?.teamName}
        nationality={fixture.riderB?.nationality}
        selected={pick === 'B'}
        called={called}
        disabled={disabled}
        onClick={() => choose('B')}
      />
    </div>
  );
}

function VsDivider() {
  return (
    <div className="flex items-center gap-2 px-1.5 py-[3px]">
      <div className="h-px flex-1 bg-black/[0.07]" />
      <span className="text-[9px] font-extrabold tracking-[0.14em] text-[#C4C4CC]">VS</span>
      <div className="h-px flex-1 bg-black/[0.07]" />
    </div>
  );
}

function PickRow({
  name,
  team,
  nationality,
  selected,
  called,
  disabled,
  onClick
}: {
  name: string;
  team?: string | null;
  nationality?: string | null;
  selected: boolean;
  called: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const { surname, firstName } = parseRiderName(name);
  const beaten = called && !selected;
  const winner = called && selected;
  const subtitle = riderSubtitle(team, nationality);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'tap flex w-full items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left',
        winner
          ? 'border-[1.5px] border-accent/50 bg-[#EAFBF1]'
          : beaten
            ? 'border border-black/[0.06] bg-[#FBFBFD] opacity-50'
            : 'border border-black/[0.08] bg-white active:bg-fill/40'
      )}
    >
      <RiderAvatar name={name} nationality={nationality} />

      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] tracking-[-0.01em] text-ink">
          <span className="font-extrabold">{surname.toUpperCase()}</span>
          {firstName ? <span className="font-medium text-[#6E6E73]"> {firstName}</span> : null}
        </div>
        {subtitle ? (
          <div
            className={cn(
              'mt-[1px] truncate text-[10.5px] font-semibold',
              beaten ? 'text-ink4' : 'text-ink2'
            )}
          >
            {subtitle}
          </div>
        ) : null}
      </div>

      {winner ? (
        <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-accent shadow-[0_6px_12px_-5px_rgba(36,197,106,0.8)]">
          <Check width={12} height={12} strokeWidth={3.4} className="text-white" />
        </span>
      ) : beaten ? (
        <span className="shrink-0 text-[11px] font-extrabold text-[#B4B4BC]">2nd</span>
      ) : (
        <span className="h-[22px] w-[22px] shrink-0 rounded-full border-2 border-black/[0.16]" />
      )}
    </button>
  );
}
